/* Progressive disclosure and feedback only. Native commerce remains the owner. */
(() => {
  'use strict';
  const root = document.querySelector('#av-pdp-public-v2[data-gp-pack="true"]');
  if (!root || root.dataset.gpEnhanced) return;
  root.dataset.gpEnhanced = 'true';
  root.querySelectorAll('[data-gp-label-link]').forEach(link => {
    link.addEventListener('click', () => {
      const facts = root.querySelector('#av-pdp-facts');
      if (!facts) return;
      facts.querySelectorAll('details[data-av-ingredients], details[data-av-decision-disclosure="nutrition"]').forEach(details => { details.open = true; });
      const heading = facts.querySelector('h2');
      if (heading) requestAnimationFrame(() => {
        // Retain the native href/hash and keyboard focus. After the disclosures
        // change layout, settle the actual target without a second animation.
        facts.scrollIntoView({ block: 'start', behavior: 'instant' });
        heading.focus({ preventScroll: true });
      });
    });
  });
  root.querySelectorAll('[data-gp-label-state]').forEach(container => {
    const img = container.querySelector('img');
    const loading = container.querySelector('[data-gp-label-loading]');
    const error = container.querySelector('[data-gp-label-error]');
    const source = container.querySelector('[data-gp-label-source]');
    const sourceUrl = source?.getAttribute('href');
    const sourceLabel = source?.querySelector('span');
    if (!img) return;
    const setState = state => {
      container.dataset.gpLabelState = state;
      if (loading) loading.hidden = state !== 'loading';
      if (error) error.hidden = state !== 'error';
      if (sourceLabel) sourceLabel.hidden = state === 'error';
      if (source && sourceUrl) {
        if (state === 'error') {
          source.removeAttribute('href');
          source.setAttribute('aria-disabled', 'true');
        } else {
          source.setAttribute('href', sourceUrl);
          source.removeAttribute('aria-disabled');
        }
      }
    };
    img.addEventListener('load', () => setState('loaded'));
    img.addEventListener('error', () => setState('error'));
    setState(img.complete ? (img.naturalWidth ? 'loaded' : 'error') : 'loading');
  });
  root.querySelectorAll('[data-gp-notify-email]').forEach(input => {
    input.addEventListener('invalid', () => input.setCustomValidity(input.dataset.invalidMessage || ''));
    input.addEventListener('input', () => input.setCustomValidity(''));
  });
  const updateEditing = () => {
    root.dataset.gpEditing = String(root.contains(document.activeElement) && document.activeElement.matches('input:not([type=hidden]):not([type=submit]):not([type=button]),textarea,[contenteditable=true]'));
  };
  root.addEventListener('focusin', updateEditing);
  root.addEventListener('focusout', () => requestAnimationFrame(updateEditing));

  if (!('IntersectionObserver' in window)) return;
  // GP launcher state helpers. Only the observed launcher is ever changed.
  function gpLauncherPolicy(mobile, purchaseVisible, editing, focused, panelOpen) {
    return mobile && (purchaseVisible || editing) && !focused && !panelOpen;
  }
  let managedLauncher = null;
  let originalDisplay = null;
  function restoreLauncher() {
    if (!managedLauncher) return;
    const style = managedLauncher.style;
    if (style.getPropertyValue('display') === 'none' && style.getPropertyPriority('display') === 'important') {
      if (originalDisplay.value) style.setProperty('display', originalDisplay.value, originalDisplay.priority);
      else style.removeProperty('display');
    }
    delete managedLauncher.dataset.gpLauncherSuppressed;
    managedLauncher = null;
    originalDisplay = null;
  }
  function setLauncherSuppressed(launcher, suppress) {
    if (!launcher || !suppress) { restoreLauncher(); return; }
    if (managedLauncher !== launcher) {
      restoreLauncher();
      managedLauncher = launcher;
      originalDisplay = { value: launcher.style.getPropertyValue('display'), priority: launcher.style.getPropertyPriority('display') };
    }
    const style = launcher.style;
    if (style.getPropertyValue('display') !== 'none' || style.getPropertyPriority('display') !== 'important') {
      originalDisplay = { value: style.getPropertyValue('display'), priority: style.getPropertyPriority('display') };
      style.setProperty('display', 'none', 'important');
    }
    launcher.dataset.gpLauncherSuppressed = 'true';
  }
  // End GP launcher state helpers.
  const controlSelector = '[data-av48-quantity], [data-add-to-cart], .gp-pdp-notify';
  // Smile replaces its lite host after the first panel open. These are the two
  // observed launcher containers, never either host or the rewards panel.
  const launcherSelector = '#smile-ui-lite-launcher-frame-container,#smile-ui-container .smile-launcher-frame-container';
  const controls = new Set(), intersections = new Set();
  let launcherFrame = 0, smileHost = null;
  const onScreen = node => {
    const box = node.getBoundingClientRect(), style = getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden' && box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < innerHeight && box.right > 0 && box.left < innerWidth;
  };
  const scheduleLauncher = () => {
    if (launcherFrame) return;
    launcherFrame = requestAnimationFrame(() => {
      launcherFrame = 0;
      if (!root.isConnected) { cleanupLauncherGuard(); return; }
      const host = document.getElementById('smile-ui-container') || document.getElementById('smile-ui-lite-container');
      if (host !== smileHost) {
        smileChanges.disconnect();
        smileHost = host;
        if (host) smileChanges.observe(host, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'aria-expanded'] });
      }
      const launcher = host?.querySelector(launcherSelector) || document.querySelector(launcherSelector);
      const focused = Boolean(launcher && (launcher === document.activeElement || launcher.contains(document.activeElement)));
      const panelOpen = Boolean(host && [...host.querySelectorAll('iframe:not(#smile-lite-launcher-frame):not(.smile-launcher-frame),[role="dialog"],[aria-modal="true"]')].some(onScreen));
      setLauncherSuppressed(launcher, gpLauncherPolicy(innerWidth < 768, [...intersections].some(onScreen), root.dataset.gpEditing === 'true', focused, panelOpen));
    });
  };
  const visibility = new IntersectionObserver(entries => {
    entries.forEach(entry => entry.isIntersecting ? intersections.add(entry.target) : intersections.delete(entry.target));
    scheduleLauncher();
  });
  const smileChanges = new MutationObserver(scheduleLauncher);
  const refreshControls = () => {
    controls.forEach(node => { if (!root.contains(node)) { visibility.unobserve(node); controls.delete(node); intersections.delete(node); } });
    root.querySelectorAll(controlSelector).forEach(node => { if (!controls.has(node)) { controls.add(node); visibility.observe(node); } });
    scheduleLauncher();
  };
  const lateNodes = new MutationObserver(mutations => {
    if (!root.isConnected) { cleanupLauncherGuard(); return; }
    if (mutations.some(mutation => root.contains(mutation.target) || [...mutation.addedNodes, ...mutation.removedNodes].some(node => node instanceof Element && (node.matches('#smile-ui-lite-container,#smile-ui-container,' + launcherSelector) || node.querySelector(launcherSelector))))) refreshControls();
  });
  function cleanupLauncherGuard() {
    visibility.disconnect(); smileChanges.disconnect(); lateNodes.disconnect();
    if (launcherFrame) cancelAnimationFrame(launcherFrame);
    document.removeEventListener('focusin', scheduleLauncher);
    document.removeEventListener('focusout', scheduleLauncher);
    removeEventListener('blur', scheduleLauncher); removeEventListener('resize', scheduleLauncher);
    removeEventListener('pageshow', scheduleLauncher); removeEventListener('pagehide', restoreLauncher);
    restoreLauncher();
  }
  lateNodes.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('focusin', scheduleLauncher);
  document.addEventListener('focusout', scheduleLauncher);
  addEventListener('blur', scheduleLauncher);
  addEventListener('resize', scheduleLauncher, { passive: true });
  addEventListener('pageshow', scheduleLauncher);
  addEventListener('pagehide', restoreLauncher);
  refreshControls();
})();
